import { useState, useCallback, useEffect, useRef } from "react";
import apiService from "~/services/service";
import { API_ROUTES } from "~/api-routes";
import API_ROUTES_V2 from "~/api-routes-v2";
import Categories from "~/components/Processes/conversations/utils/categories.json";

// Base32 alphabet for ULID conversion
const base32 = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

/**
 * Convert ULID to UUID
 */
const ulidToUuid = (ulidStr) => {
  if (ulidStr.length !== 26) throw new Error("Invalid ULID");

  function decodeBase32(str) {
    let num = 0;
    for (let i = 0; i < str.length; i++) {
      num = num * 32 + base32.indexOf(str[i]);
    }
    return num;
  }

  const timestamp = decodeBase32(ulidStr.slice(0, 10));
  const randPart = ulidStr.slice(10);
  let randNum = BigInt(0);
  for (let i = 0; i < randPart.length; i++) {
    randNum = randNum * BigInt(32) + BigInt(base32.indexOf(randPart[i]));
  }

  const tsHex = timestamp.toString(16).padStart(12, "0");
  const randHex = randNum.toString(16).padStart(20, "0").slice(0, 20);
  const hex = tsHex + randHex;

  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join("-");
};

/**
 * Generates breadcrumb string based on metadata and fetched data
 * Format: "Core Entity Information / Entity Information / [Officer/Shareholder] / name"
 * For payslip: "Payroll / [year] / [payslip_name]"
 * For ad hoc request: "Ad Hoc Requests / [year] / [reason] / Request [request_id]"
 */
const getCategoryName = (categoryCode) => {
  if (!categoryCode) return null;
  const cat = Categories?.find((c) => c.code === categoryCode);
  return cat?.name || null;
};

const generateBreadcrumb = (item) => {
  const breadcrumbParts = [];
  const categoryName = getCategoryName(item.category);

  // Add officer breadcrumb with core entity info
  if (item.officer_data?.data?.name && item.core_entity_info) {
    breadcrumbParts.push("Core Entity Information");
    breadcrumbParts.push("Entity Information");
    breadcrumbParts.push("Officer");
    breadcrumbParts.push(item.officer_data.data.name);
    if (categoryName) breadcrumbParts.push(categoryName);
  }
  // Add officer breadcrumb without core entity info
  else if (item.officer_data?.data) {
    breadcrumbParts.push("Core Entity Information");
    if (item.officer_data.data.type === "officer") {
      breadcrumbParts.push("Officer");
    } else {
      breadcrumbParts.push("Power of Attorney");
    }
    if (item.officer_data.data.name) {
      breadcrumbParts.push(item.officer_data.data.name);
    }
    if (categoryName) breadcrumbParts.push(categoryName);
  }
  // Add shareholder breadcrumb with core entity info
  else if (item.shareHolder_data?.data && item.core_entity_info) {
    breadcrumbParts.push("Core Entity Information");
    breadcrumbParts.push("Entity Information");
    breadcrumbParts.push("Shareholder");
    if (item.shareHolder_data.data.legal_name) {
      breadcrumbParts.push(item.shareHolder_data.data.legal_name);
    }
    if (item.shareHolder_data.data.name) {
      breadcrumbParts.push(item.shareHolder_data.data.name);
    }
    if (categoryName) breadcrumbParts.push(categoryName);
  }
  // Add shareholder breadcrumb without core entity info
  else if (item.shareHolder_data?.data) {
    breadcrumbParts.push("Core Entity Information");
    breadcrumbParts.push("Shareholder");

    if (item.shareHolder_data.data.legal_name) {
      breadcrumbParts.push(item.shareHolder_data.data.legal_name);
    }
    if (item.shareHolder_data.data.name) {
      breadcrumbParts.push(item.shareHolder_data.data.name);
    }
    if (categoryName) breadcrumbParts.push(categoryName);
  }
  // Add director breadcrumb with core entity info
  else if (item.director_data?.data && item.core_entity_info) {
    breadcrumbParts.push("Core Entity Information");
    breadcrumbParts.push("Entity Information");
    breadcrumbParts.push("Director");
    const directorName = item.director_data.data.preferred_name
      ? item.director_data.data.preferred_name
      : `${item.director_data.data.name}${item.director_data.data.last_name ? " " + item.director_data.data.last_name : ""}`;
    breadcrumbParts.push(directorName);
    if (categoryName) breadcrumbParts.push(categoryName);
  }
  // Add director breadcrumb without core entity info
  else if (item.director_data?.data) {
    breadcrumbParts.push("Core Entity Information");
    breadcrumbParts.push("Director");
    const directorName = item.director_data.data.preferred_name
      ? item.director_data.data.preferred_name
      : `${item.director_data.data.name}${item.director_data.data.last_name ? " " + item.director_data.data.last_name : ""}`;
    breadcrumbParts.push(directorName);
    if (categoryName) breadcrumbParts.push(categoryName);
  }
  // Add ad hoc request breadcrumb
  else if (item.request_data || item.request_reason) {
    breadcrumbParts.push("Ad Hoc Requests");
    // Year from tag, or fallback to createdAt from request data
    const year =
      item.year ||
      (item.request_data?.data?.createdAt
        ? new Date(item.request_data.data.createdAt).getFullYear().toString()
        : null);
    if (year) {
      breadcrumbParts.push(year);
    }
    const reason =
      item.request_data?.data?.reason ||
      item.request_data?.data?.request_reason ||
      item.request_reason;
    if (reason) {
      // Only format if it looks like a code (e.g., "SERVICE_QUERY" -> "Service Query")
      const isCode = reason.includes("_") && reason === reason.toUpperCase();
      const formattedReason = isCode
        ? reason
            .split("_")
            .map(
              (word) =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
            )
            .join(" ")
        : reason;
      breadcrumbParts.push(formattedReason);
    }
    // Append request_id if available (e.g., "Request10")
    const requestId = item.request_data?.data?.request_id;
    if (requestId) {
      breadcrumbParts.push(requestId);
    }
  }
  // Add process/payslip breadcrumb (payslip, books, EOR, etc. all come through process API)
  else if (
    item.payslip_data?.data?.serviceData?.name ||
    item.process_data?.data?.serviceData?.name
  ) {
    const processData = item.payslip_data?.data || item.process_data?.data;
    breadcrumbParts.push(processData.serviceData.name);
    if (item.year) {
      breadcrumbParts.push(item.year);
    }
    if (processData.name) {
      // Extract period code from start_date (YYYYMM format)
      let processName = processData.name;
      if (processData.start_date) {
        const periodCode =
          new Date(processData.start_date).getFullYear() +
          new Date(processData.start_date).toLocaleDateString("default", {
            month: "2-digit",
          });
        // Only append period code if it's not already in the name
        if (!processName.includes(periodCode)) {
          processName = `${processName} (${periodCode})`;
        }
      }
      breadcrumbParts.push(processName);
    }
  }
  // Fallback for process/payslip when process_data exists but serviceData is missing
  else if (item.process_data?.data?.name || item.payslip_data?.data?.name) {
    const processData = item.payslip_data?.data || item.process_data?.data;
    if (item.year) {
      breadcrumbParts.push(item.year);
    }
    if (processData.name) {
      breadcrumbParts.push(processData.name);
    }
  }
  // Fallback for process/payslip when API call failed but metadata has process/payslip key
  else if (item.process || item.payslip) {
    if (item.year) {
      breadcrumbParts.push(item.year);
    }
  }

  // Add core entity info only
  else if (item.core_entity_info) {
    breadcrumbParts.push("Core Entity Information");
    breadcrumbParts.push("Entity Information");
  }
  // Fallback: use category name if available
  else if (item._category) {
    const categoryName = item._categoryName || item._category;
    breadcrumbParts.push(categoryName);
    if (item._subCategoryName) {
      breadcrumbParts.push(item._subCategoryName);
    }
  }
  return breadcrumbParts.join(" / ");
};

/**
 * Hook to process metadata array and convert ULID IDs to UUIDs
 * Also fetches data from backend for officer, process, shareHolder, and entity
 * Generates breadcrumbs for each metadata item
 *
 * @param {Array} metadataArray - Array of metadata objects to process
 * @returns {Object} { processedData, loading, error }
 */
const useMetadataProcessor = (metadataArray = []) => {
  const [processedData, setProcessedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const prevInputRef = useRef(null);

  const processMetadata = useCallback(async () => {
    if (!Array.isArray(metadataArray) || metadataArray.length === 0) {
      setProcessedData([]);
      prevInputRef.current = null;
      return;
    }

    // Avoid re-processing if the input hasn't actually changed
    const inputKey = JSON.stringify(
      metadataArray.map((item) => item.id || item.blobName || item.name),
    );
    if (inputKey === prevInputRef.current) {
      return;
    }
    prevInputRef.current = inputKey;

    setLoading(true);
    setError(null);

    try {
      const processedItems = await Promise.all(
        metadataArray.map(async (item) => {
          // Extract metadata from nested metadata object or use item directly
          const metadata = item.metadata || item;
          // Resolve category/subCategory names for fallback breadcrumb
          const catCode = item.category || metadata.category;
          const subCatCode =
            item.subCategory || metadata.subCategory || metadata.sub_category;
          const catObj = Categories?.find((c) => c.code === catCode);
          const subCatObj = catObj?.subCategory?.find(
            (s) => s.code === subCatCode,
          );
          const processedItem = {
            filename: item.filename || item.name,
            ...metadata,
            _category: catCode,
            _categoryName: catObj?.name,
            _subCategoryName: subCatObj?.name,
          };

          // Convert ULID to UUID for officer
          if (metadata.officer) {
            try {
              processedItem.officer_uuid = ulidToUuid(metadata.officer);
              const response = await apiService(
                API_ROUTES_V2.FETCH_OFFICER,
                "GET",
                { replace: { id: processedItem.officer_uuid } },
              );
              processedItem.officer_data = response;
            } catch (err) {
              console.error("Error fetching officer data:", err);
            }
          }

          // Convert ULID to UUID for payslip or process (both use FETCH_PROCESS API)
          // Payslip, Books, EOR, etc. all come through the process API
          if (metadata.payslip) {
            try {
              processedItem.payslip_uuid = ulidToUuid(metadata.payslip);
              const response = await apiService(
                API_ROUTES_V2.FETCH_PROCESS,
                "GET",
                { replace: { id: processedItem.payslip_uuid } },
              );
              processedItem.payslip_data = response;
            } catch (err) {
              console.error("Error fetching payslip data:", err);
            }
          }

          // Convert ULID to UUID for process
          if (metadata.process) {
            try {
              processedItem.process_uuid = ulidToUuid(metadata.process);
              const response = await apiService(
                API_ROUTES_V2.FETCH_PROCESS,
                "GET",
                { replace: { id: processedItem.process_uuid } },
              );
              processedItem.process_data = response;
            } catch (err) {
              console.error("Error fetching process data:", err);
            }
          }

          // Convert ULID to UUID for escalated_process (treat same as process)
          if (metadata.escalated_process && !metadata.process) {
            try {
              processedItem.process_uuid = ulidToUuid(
                metadata.escalated_process,
              );
              const response = await apiService(
                API_ROUTES_V2.FETCH_PROCESS,
                "GET",
                { replace: { id: processedItem.process_uuid } },
              );
              processedItem.process_data = response;
            } catch (err) {
              console.error("Error fetching escalated process data:", err);
            }
          }

          // Convert ULID to UUID for shareHolder
          if (metadata.shareHolder) {
            try {
              processedItem.shareHolder_uuid = ulidToUuid(metadata.shareHolder);
              // Get entity UUID for the shareholder API call
              const entity_id = metadata.entity
                ? ulidToUuid(metadata.entity)
                : "";
              const response = await apiService(
                `${API_ROUTES_V2.FETCH_SHAREHOLDER}`,
                "GET",
                {
                  replace: {
                    id: processedItem.shareHolder_uuid,
                    entity_id: entity_id,
                  },
                },
              );
              processedItem.shareHolder_data = response;
            } catch (err) {
              console.error("Error fetching shareholder data:", err);
            }
          }

          // Convert ULID to UUID for director
          if (metadata.director) {
            try {
              processedItem.director_uuid = ulidToUuid(metadata.director);
              // Get entity UUID for the director API call
              const entity_id = metadata.entity
                ? ulidToUuid(metadata.entity)
                : "";
              const response = await apiService(
                API_ROUTES_V2.FETCH_DIRECTOR,
                "GET",
                {
                  replace: {
                    id: processedItem.director_uuid,
                    entity_id: entity_id,
                  },
                },
              );
              processedItem.director_data = response;
            } catch (err) {
              console.error("Error fetching director data:", err);
            }
          }

          // Convert ULID to UUID for entity (only if core_entity_info exists)
          if (metadata.entity && metadata.core_entity_info) {
            try {
              processedItem.entity_uuid = ulidToUuid(metadata.entity);
              const response = await apiService(
                API_ROUTES_V2.FETCH_ENTITY,
                "GET",
                { replace: { id: processedItem.entity_uuid } },
              );
              processedItem.entity_data = response;
            } catch (err) {
              console.error("Error fetching entity data:", err);
            }
          }

          // Convert ULID to UUID for ad hoc request (only if request_reason is present)
          if (metadata.request && metadata.request_reason) {
            try {
              processedItem.request_uuid = ulidToUuid(metadata.request);
              const response = await apiService(
                API_ROUTES_V2.FETCH_REQUEST,
                "GET",
                { replace: { id: processedItem.request_uuid } },
              );
              processedItem.request_data = response;
            } catch (err) {
              console.error("Error fetching request data:", err);
            }
          }

          // Generate breadcrumb
          processedItem.breadcrumb = generateBreadcrumb(processedItem);

          return processedItem;
        }),
      );

      // Transform to include breadcrumb at top level and return full document data
      const enrichedItems = metadataArray.map((originalItem, index) => {
        const processedItem = processedItems[index];
        return {
          ...originalItem,
          breadcrumb: processedItem.breadcrumb,
          // Pass through enrichment data for folder navigation
          process_uuid: processedItem.process_uuid,
          payslip_uuid: processedItem.payslip_uuid,
          process_data: processedItem.process_data,
          payslip_data: processedItem.payslip_data,
          officer_data: processedItem.officer_data,
          officer_uuid: processedItem.officer_uuid,
          director_data: processedItem.director_data,
          director_uuid: processedItem.director_uuid,
          shareHolder_data: processedItem.shareHolder_data,
          shareHolder_uuid: processedItem.shareHolder_uuid,
          request_data: processedItem.request_data,
          request_uuid: processedItem.request_uuid,
        };
      });

      setProcessedData(enrichedItems);
      setLoading(false);
    } catch (err) {
      const errorMessage = err.message || "Error processing metadata";
      setError(errorMessage);
      setLoading(false);
    }
  }, [metadataArray]);

  // Auto-process when metadata changes
  useEffect(() => {
    processMetadata();
  }, [processMetadata]);

  return {
    processedData,
    loading,
    error,
    setLoading,
  };
};

export default useMetadataProcessor;
